/**
 * Validation utilities using Zod
 * Provides schemas and helper functions for form validation
 */

import { z } from 'zod';
import { sanitizeString } from '@/lib/utils/formValidation';

/**
 * Contact form schema with validation rules
 */
export const ContactFormSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .transform(sanitizeString),

  email: z
    .string()
    .email('Please enter a valid email address')
    .toLowerCase()
    .trim(),

  phone: z
    .string()
    .regex(
      /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
      'Please enter a valid phone number'
    )
    .optional()
    .nullable()
    .transform(val => val || undefined),

  company: z
    .string()
    .max(100, 'Company name must be less than 100 characters')
    .optional()
    .transform(val => sanitizeString(val || '')),

  message: z
    .string()
    .min(10, 'Message must be at least 10 characters')
    .max(2000, 'Message must be less than 2000 characters')
    .transform(sanitizeString),

  services: z.array(z.string()).optional().default([]),

  // Ensures CSRF token is present
  csrf_token: z.string().min(1, 'CSRF token is required'),

  // Additional marketing preferences
  subscribe_to_newsletter: z.boolean().optional().default(false),

  // Hidden honeypot field to catch bots (should remain empty)
  _hp: z.string().refine(val => val === '', {
    message: 'Honeypot field should be empty',
  }),
});

/**
 * Email subscription schema
 */
export const EmailSubscriptionSchema = z.object({
  email: z
    .string()
    .email('Please enter a valid email address')
    .toLowerCase()
    .trim(),

  name: z
    .string()
    .max(100, 'Name must be less than 100 characters')
    .optional()
    .transform(val => sanitizeString(val || '')),

  source: z.string().optional(),

  csrf_token: z.string().min(1, 'CSRF token is required'),

  // Hidden honeypot field
  _hp: z.string().refine(val => val === '', {
    message: 'Honeypot field should be empty',
  }),
});

/**
 * Blog post schema
 */
export const BlogPostSchema = z.object({
  title: z
    .string()
    .min(5, 'Title must be at least 5 characters')
    .max(150, 'Title must be less than 150 characters'),

  slug: z
    .string()
    .min(5, 'Slug must be at least 5 characters')
    .max(150, 'Slug must be less than 150 characters')
    .regex(
      /^[a-z0-9-]+$/,
      'Slug can only contain lowercase letters, numbers, and hyphens'
    ),

  content: z.string().min(100, 'Content must be at least 100 characters'),

  excerpt: z
    .string()
    .min(10, 'Excerpt must be at least 10 characters')
    .max(300, 'Excerpt must be less than 300 characters'),

  is_published: z.boolean(),

  categories: z.array(z.string()).optional().nullable(),

  author_name: z.string().min(2, 'Author name is required'),

  featured_image: z
    .string()
    .url('Featured image must be a valid URL')
    .optional()
    .nullable(),
});

/**
 * Login form schema
 */
export const LoginSchema = z.object({
  email: z
    .string()
    .email('Please enter a valid email address')
    .toLowerCase()
    .trim(),

  password: z.string().min(8, 'Password must be at least 8 characters'),

  csrf_token: z.string().min(1, 'CSRF token is required'),
});

/**
 * Type inference from schemas
 */
export type ContactFormData = z.infer<typeof ContactFormSchema>;
export type EmailSubscriptionData = z.infer<typeof EmailSubscriptionSchema>;
export type BlogPostData = z.infer<typeof BlogPostSchema>;
export type LoginData = z.infer<typeof LoginSchema>;

/**
 * Validate data against a Zod schema
 * @param schema Zod schema to validate against
 * @param data Data to validate
 * @returns Object with success status, validated data, and errors
 */
export function validateWithSchema<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown
): { success: boolean; data?: z.infer<T>; errors?: Record<string, string> } {
  try {
    const validatedData = schema.parse(data);
    return { success: true, data: validatedData };
  } catch (error) {
    if (error instanceof z.ZodError) {
      // Convert Zod errors to a more user-friendly format
      const fieldErrors: Record<string, string> = {};

      for (const issue of error.errors) {
        // Get the field path as a string (e.g., "address.city" -> "address.city")
        const path = issue.path.join('.');
        fieldErrors[path] = issue.message;
      }

      return { success: false, errors: fieldErrors };
    }

    // If it's not a Zod error, return a generic error
    return {
      success: false,
      errors: { _general: 'An unexpected error occurred during validation' },
    };
  }
}

/**
 * Specialized validator for forms
 * @param formData FormData object to validate
 * @param schema Zod schema to validate against
 * @returns Object with success status, validated data, and errors
 */
export function validateForm(formData: FormData, schema: z.ZodType) {
  // Convert FormData to a plain object
  const dataObject: Record<string, unknown> = {};

  formData.forEach((value, key) => {
    // Handle checkbox values
    if (dataObject[key] !== undefined) {
      if (!Array.isArray(dataObject[key])) {
        dataObject[key] = [dataObject[key]];
      }
      (dataObject[key] as unknown[]).push(value);
    } else {
      // Convert 'true'/'false' strings to actual booleans for checkboxes
      if (value === 'true') dataObject[key] = true;
      else if (value === 'false') dataObject[key] = false;
      else dataObject[key] = value;
    }
  });

  return validateWithSchema(schema, dataObject);
}

/**
 * User registration schema
 */
export const RegistrationSchema = z
  .object({
    name: z.string().min(2, 'Name must be at least 2 characters'),
    email: z.string().email('Please enter a valid email address'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number'),
    passwordConfirm: z.string(),
  })
  .refine(data => data.password === data.passwordConfirm, {
    message: 'Passwords do not match',
    path: ['passwordConfirm'],
  });
